import React, { useEffect } from "react";
import { useAtom } from "jotai";
import {
  Box,
  FormControl,
  FormLabel,
  Select,
  Text,
  VStack,
  Heading,
  Divider,
} from "@chakra-ui/react";
import { AppStateAtom, DateFormatAtom } from "../../store/AppStateAtom";
import { DateFormat, formatDate } from "../../utils/DateUtils";

const dateFormatOptions: { value: DateFormat; label: string }[] = [
  { value: "MM/DD/YYYY", label: "MM/DD/YYYY - US Format" },
  { value: "DD/MM/YYYY", label: "DD/MM/YYYY - European Format" },
  { value: "YYYY-MM-DD", label: "YYYY-MM-DD - ISO Format" },
  { value: "DD-MMM-YYYY", label: "DD-MMM-YYYY - Long Format" },
];

export const DateFormatSelector = () => {
  const [dateFormat, setDateFormat] = useAtom(DateFormatAtom);
  const [appState, setAppState] = useAtom(AppStateAtom);

  // Keep the AppState dateFormat in sync with our persisted DateFormatAtom
  useEffect(() => {
    if (dateFormat !== appState.dateFormat) {
      setAppState((prev) => ({ ...prev, dateFormat }));
    }
  }, [dateFormat, appState.dateFormat, setAppState]);

  const handleFormatChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newFormat = e.target.value as DateFormat;
    setDateFormat(newFormat);
  };

  // Example date for preview
  const today = new Date();
  const sampleDate = new Date(2023, 6, 15); // July 15, 2023

  return (
    <Box p={4} borderRadius="md" shadow="sm" width="100%">
      <VStack spacing={4} align="flex-start">
        <Heading size="md">Date Format Settings</Heading>
        <Divider />

        <FormControl>
          <FormLabel>Select your preferred date format</FormLabel>
          <Select
            value={dateFormat}
            onChange={handleFormatChange}
            maxWidth="300px"
          >
            {dateFormatOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </FormControl>

        <Box>
          <Text fontWeight="bold">Preview:</Text>
          <Text>Today: {formatDate(today, dateFormat)}</Text>
          <Text>Sample Date: {formatDate(sampleDate, dateFormat)}</Text>
        </Box>
      </VStack>
    </Box>
  );
};

export default DateFormatSelector;
